import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { devicesAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/formatDateTime';
import {
  CpuChipIcon,
  VideoCameraIcon,
  SignalIcon,
  SignalSlashIcon,
  ServerStackIcon,
  FireIcon,
  ClockIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
  RectangleStackIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export default function DevicesPage() {
  const { selectedSite } = useSiteStore();
  const [settingsDevice, setSettingsDevice] = useState(null); // device whose Engine Settings modal is open
  const [restartPending, setRestartPending] = useState({});   // deviceId -> true after saving a Tier B setting

  const { data, isLoading } = useQuery({
    queryKey: ['ai-devices', selectedSite?.id],
    queryFn: () => devicesAPI.getAIDevices(selectedSite?.id ? { siteId: selectedSite.id } : undefined),
    // Metrics (CPU/GPU) update with each device heartbeat (~30s) — poll to match.
    refetchInterval: 30_000,
  });

  const devices = data?.data?.data || [];
  const onlineCount = devices.filter((d) => d.status === 'online').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">AI Devices</h1>
          <p className="text-sm text-slate-500 mt-1">
            Edge AI engines processing your camera streams
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="badge badge-success badge-dot">{onlineCount} online</span>
          <span className="badge badge-neutral badge-dot">{devices.length - onlineCount} offline</span>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass-card p-5 animate-pulse h-64" />
          ))}
        </div>
      ) : devices.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <ServerStackIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-700">No AI devices</h3>
          <p className="text-sm text-slate-500 mt-1">
            No AI engines are registered{selectedSite ? ' for this site' : ''} yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              restartPending={!!restartPending[device.id]}
              onOpenSettings={() => setSettingsDevice(device)}
              onRestarted={() =>
                setRestartPending((prev) => ({ ...prev, [device.id]: false }))
              }
            />
          ))}
        </div>
      )}

      {settingsDevice && (
        <EngineSettingsModal
          device={settingsDevice}
          onClose={() => setSettingsDevice(null)}
          onSaved={({ restartRequired }) => {
            if (restartRequired) {
              setRestartPending((prev) => ({ ...prev, [settingsDevice.id]: true }));
            }
          }}
        />
      )}
    </div>
  );
}

function DeviceCard({ device, restartPending, onOpenSettings, onRestarted }) {
  const online = device.status === 'online';
  const specs = device.hardwareSpecs;
  const [confirmRestart, setConfirmRestart] = useState(false);

  const restartMutation = useMutation({
    mutationFn: () => devicesAPI.restartAIDevice(device.id, 'Restart requested from Devices page'),
    onSuccess: () => {
      toast.success('Restart command sent — cameras reconnect in a few minutes');
      setConfirmRestart(false);
      onRestarted();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to send restart command');
      setConfirmRestart(false);
    },
  });

  return (
    <div className="glass-card p-5 transition-all duration-300">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className={clsx(
            'w-12 h-12 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0 bg-gradient-to-br',
            online ? 'from-emerald-500 to-emerald-600' : 'from-slate-400 to-slate-500'
          )}
        >
          <CpuChipIcon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 truncate">{device.name}</h3>
          <p className="text-xs text-slate-500 capitalize">
            {device.type?.replace('_', ' ') || 'Edge device'}
            {device.site?.name ? ` · ${device.site.name}` : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={clsx('badge badge-sm badge-dot', online ? 'badge-success' : 'badge-neutral')}>
            {online ? 'Online' : 'Offline'}
          </span>
          {restartPending && (
            <span className="badge badge-sm badge-warning flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3 h-3" />
              Restart pending
            </span>
          )}
        </div>
      </div>

      {/* Info row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <VideoCameraIcon className="w-3.5 h-3.5" />
          {device._count?.cameras ?? 0} cameras
        </span>
        {device.ipAddress && (
          <span className="flex items-center gap-1">
            <GlobeAltIcon className="w-3.5 h-3.5" />
            {device.ipAddress}
          </span>
        )}
        <span className="flex items-center gap-1">
          <ClockIcon className="w-3.5 h-3.5" />
          {device.lastHeartbeat ? formatDateTime(device.lastHeartbeat) : 'Never'}
        </span>
      </div>

      {/* Hardware specs (static capability report from engine registration) */}
      {specs ? (
        <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500">
              <CpuChipIcon className="w-3.5 h-3.5" /> CPU
            </span>
            <span className="font-semibold text-slate-700">
              {specs.cpuCoresUsable}/{specs.cpuCoresTotal} cores
              {specs.cpuQuotaCores != null && ` · quota ${specs.cpuQuotaCores}`}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500">
              <CircleStackIcon className="w-3.5 h-3.5" /> RAM
            </span>
            <span className="font-semibold text-slate-700">
              {specs.ramTotalMb ? `${Math.round(specs.ramTotalMb / 1024)} GB` : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5 text-slate-500">
              <RectangleStackIcon className="w-3.5 h-3.5" /> GPU
            </span>
            <span className="font-semibold text-slate-700 truncate max-w-[60%] text-right">
              {specs.gpus?.length
                ? specs.gpus.map((g) => `${g.name} (${Math.round(g.vramTotalMb / 1024)}GB)`).join(', ')
                : 'None'}
            </span>
          </div>
          {specs.runtime && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Runtime</span>
              <span className="badge badge-sm badge-neutral">{specs.runtime}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 rounded-lg p-3 mb-3 text-xs text-slate-400">
          Hardware specs not reported yet — appears after the engine restarts on a
          version that sends its capability report.
        </div>
      )}

      {/* Resource usage */}
      <div className="grid grid-cols-2 gap-2">
        <UsageBar label="CPU" value={online ? device.cpuUsage : null} />
        <UsageBar label="GPU" value={online ? device.gpuUsage : null} />
        <UsageBar label="Memory" value={online ? device.memoryUsage : null} />
        <UsageBar label="Disk" value={online ? device.diskUsage : null} />
      </div>

      {/* Temperature */}
      {online && device.temperature != null && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-slate-500">
          <FireIcon className={clsx('w-3.5 h-3.5', device.temperature >= 80 ? 'text-red-500' : device.temperature >= 65 ? 'text-amber-500' : 'text-slate-400')} />
          GPU temperature: <span className="font-semibold text-slate-700">{Math.round(device.temperature)}°C</span>
        </div>
      )}

      {/* Connectivity + actions */}
      <div className="flex items-center justify-between mt-3">
        {online ? (
          <span className="flex items-center gap-1.5 text-xs text-emerald-600">
            <SignalIcon className="w-3.5 h-3.5" /> Heartbeat active
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <SignalSlashIcon className="w-3.5 h-3.5" /> No heartbeats
          </span>
        )}
        <div className="flex items-center gap-2">
          {online && (
            confirmRestart ? (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => restartMutation.mutate()}
                  disabled={restartMutation.isPending}
                  className="btn btn-danger text-xs py-1.5 px-3"
                >
                  {restartMutation.isPending ? 'Sending…' : 'Confirm restart'}
                </button>
                <button onClick={() => setConfirmRestart(false)} className="btn btn-secondary text-xs py-1.5 px-2">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRestart(true)}
                className="btn btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                title="Restart the AI engine on this device (cameras reconnect in a few minutes)"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Restart
              </button>
            )
          )}
          <button
            onClick={onOpenSettings}
            className="btn btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <Cog6ToothIcon className="w-4 h-4" />
            Engine Settings
          </button>
        </div>
      </div>
    </div>
  );
}

/** Human labels + grouping for the engine settings form. Ranges/types come from
 *  the backend schema (single source of truth) — this map only adds display info. */
const SETTING_LABELS = {
  INFERENCE_EXECUTOR_WORKERS: { label: 'Inference threads', group: 'Processing', hint: 'Thread pool running AI model inference' },
  INFERENCE_MODEL_LOCKS: { label: 'Model locks', group: 'Processing', hint: 'Serialize access per model type (safer)' },
  QUEUE_NUM_WORKERS: { label: 'Initial queue workers', group: 'Processing', hint: 'Workers started at boot (autoscaler adjusts after)' },
  QUEUE_MAX_WORKERS_CEILING: { label: 'Max queue workers', group: 'Processing', hint: 'Autoscaler upper limit' },
  QUEUE_MAX_SIZE: { label: 'Queue size', group: 'Processing', hint: 'Frames buffered before load-shedding' },
  KENSAFETY_CAPTURE_MODE: { label: 'Capture mode', group: 'Capture', hint: 'thread (default) or per-camera subprocess' },
  PROBE_TIMEOUT_S: { label: 'Probe timeout (s)', group: 'Capture', hint: 'ffprobe timeout before NVDEC fallback' },
  MAX_CAPTURE_ERRORS: { label: 'Max capture errors', group: 'Capture', hint: 'Consecutive misses before reconnect' },
  FREEZE_TIMEOUT_SECONDS: { label: 'Freeze timeout (s)', group: 'Capture', hint: 'Identical-frame window before reconnect' },
  RTSP_STIMEOUT_US: { label: 'RTSP timeout (µs)', group: 'Capture', hint: 'RTSP socket timeout' },
  MAX_BACKOFF_CYCLES: { label: 'Max backoff cycles', group: 'Capture', hint: 'Reconnect backoff escalations' },
  CAMERA_STREAM_STALE_SECONDS: { label: 'Stream stale window (s)', group: 'Status', hint: 'No valid frame for this long → camera shows offline' },
  HEARTBEAT_INTERVAL: { label: 'Heartbeat interval (s)', group: 'Status', hint: 'Device→backend heartbeat cadence' },
  HYBRID_IMAGE_RETENTION_DAYS: { label: 'Snapshot retention (days)', group: 'Storage', hint: 'Days to keep alert images on the edge device before auto-delete' },
};

function EngineSettingsModal({ device, onClose, onSaved }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({}); // key -> edited value (only touched keys)

  const { data, isLoading } = useQuery({
    queryKey: ['engine-settings', device.id],
    queryFn: () => devicesAPI.getEngineSettings(device.id),
  });

  const settings = data?.data?.engineSettings || {};
  const schema = data?.data?.schema || {};
  const recommended = data?.data?.recommended || {};
  const totalFps = data?.data?.totalFps;
  const cameraCount = data?.data?.cameraCount;

  const saveMutation = useMutation({
    mutationFn: (engineSettings) => devicesAPI.updateEngineSettings(device.id, engineSettings),
    onSuccess: (res) => {
      const restartRequired = res?.data?.restartRequired;
      toast.success(
        restartRequired
          ? 'Saved — takes effect after the engine restarts'
          : 'Saved — applied live via hot reload'
      );
      queryClient.invalidateQueries({ queryKey: ['engine-settings', device.id] });
      queryClient.invalidateQueries({ queryKey: ['ai-devices'] });
      setDraft({});
      onSaved({ restartRequired });
      onClose();
    },
    onError: (err) => {
      const details = err.response?.data?.details;
      toast.error(details?.length ? details.join('; ') : 'Failed to save settings');
    },
  });

  const handleSave = () => {
    if (Object.keys(draft).length === 0) {
      onClose();
      return;
    }
    saveMutation.mutate(draft);
  };

  // Group keys by section for display
  const groups = {};
  Object.keys(schema).forEach((key) => {
    const group = SETTING_LABELS[key]?.group || 'Other';
    (groups[group] = groups[group] || []).push(key);
  });

  const currentValue = (key) =>
    key in draft ? draft[key] : (settings[key] ?? '');

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ maxHeight: 'min(85vh, calc(100vh - 2rem))' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — always visible, never scrolls away */}
        <div className="flex-shrink-0 border-b px-6 py-4 flex items-center justify-between rounded-t-2xl bg-white">
          <div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Cog6ToothIcon className="w-5 h-5 text-slate-500" />
              Engine Settings — {device.name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Empty field = no override (engine falls back to its .env / auto-sized default).
              Settings marked <span className="text-amber-600 font-medium">restart</span> apply after the engine restarts.
            </p>
            {totalFps != null && (
              <p className="text-xs text-slate-400 mt-0.5">
                Current load: {cameraCount} cameras · {Math.round(totalFps)} frames/sec —
                recommendations are auto-sized for this load.
              </p>
            )}
          </div>
          <button onClick={onClose} className="icon-btn flex-shrink-0 ml-4">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body — only this section scrolls */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 bg-slate-100 rounded-lg" />)}
            </div>
          ) : (
            Object.entries(groups).map(([group, keys]) => (
              <div key={group}>
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">{group}</h3>
                <div className="space-y-3">
                  {keys.map((key) => {
                    const spec = schema[key];
                    const meta = SETTING_LABELS[key] || { label: key };
                    return (
                      <div key={key} className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            {meta.label}
                            {spec.restartRequired && (
                              <span className="badge badge-sm badge-warning">restart</span>
                            )}
                          </div>
                          {meta.hint && <p className="text-xs text-slate-400 truncate">{meta.hint}</p>}
                          {recommended[key] != null && (
                            <p className="text-xs text-blue-500">
                              recommended: {recommended[key]}
                              {settings[key] != null && settings[key] !== recommended[key] && ' (override active)'}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 w-40">
                          {spec.type === 'bool' ? (
                            <select
                              className="input py-1.5 px-2 text-sm w-full"
                              value={String(currentValue(key))}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  [key]: e.target.value === '' ? null : e.target.value === 'true',
                                }))
                              }
                            >
                              <option value="">default</option>
                              <option value="true">enabled</option>
                              <option value="false">disabled</option>
                            </select>
                          ) : spec.type === 'enum' ? (
                            <select
                              className="input py-1.5 px-2 text-sm w-full"
                              value={currentValue(key)}
                              onChange={(e) =>
                                setDraft((d) => ({ ...d, [key]: e.target.value === '' ? null : e.target.value }))
                              }
                            >
                              <option value="">default</option>
                              {spec.values.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                          ) : (
                            <input
                              type="number"
                              min={spec.min}
                              max={spec.max}
                              placeholder={recommended[key] != null ? String(recommended[key]) : `${spec.min}–${spec.max}`}
                              className="input py-1.5 px-2 text-sm w-full"
                              value={currentValue(key)}
                              onChange={(e) =>
                                setDraft((d) => ({
                                  ...d,
                                  [key]: e.target.value === '' ? null : Number(e.target.value),
                                }))
                              }
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer — always visible, never scrolls away */}
        <div className="flex-shrink-0 border-t px-6 py-4 flex justify-end gap-2 rounded-b-2xl bg-white">
          <button onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || Object.keys(draft).length === 0}
            className="btn btn-primary text-sm"
          >
            {saveMutation.isPending ? 'Saving…' : `Save${Object.keys(draft).length ? ` (${Object.keys(draft).length})` : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function UsageBar({ label, value }) {
  const pct = value == null ? null : Math.max(0, Math.min(100, Math.round(value)));
  const color =
    pct == null ? 'bg-slate-300'
    : pct >= 90 ? 'bg-red-500'
    : pct >= 70 ? 'bg-amber-500'
    : 'bg-emerald-500';
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-medium text-slate-500">{label}</span>
        <span className="text-xs font-bold text-slate-800">{pct == null ? '—' : `${pct}%`}</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${pct ?? 0}%` }} />
      </div>
    </div>
  );
}
